import type { QrStyle } from '../../types.js';

export type QrDesignQuality = {
  pass: boolean;
  contrast: number;
  issues: Array<[english: string, chinese: string]>;
};

export function evaluateQrStyle(style: QrStyle): QrDesignQuality {
  const contrast = colorContrast(style.darkColor, style.lightColor);
  const issues: QrDesignQuality['issues'] = [];
  if (contrast < 4.5) issues.push(['Increase module-to-panel contrast', '提高码点与安全区的颜色对比']);
  if (style.margin < 4) issues.push(['Use at least a 4-module quiet zone', '四周至少保留 4 模块留白']);
  if (style.background && style.errorCorrection !== 'H') issues.push(['Artwork requires H error correction', '使用底图时需要 H 级容错']);
  if (style.background && style.background.qrScale > 0.84) issues.push(['Reduce QR size on artwork', '缩小底图上的二维码尺寸']);
  return { pass: issues.length === 0, contrast, issues };
}

export async function renderMatureQrSvg(url: string, style: QrStyle): Promise<string | null> {
  const isSupportedModule = style.moduleStyle === 'square' || style.moduleStyle === 'rounded' || style.moduleStyle === 'dots';
  if (style.background || style.frameStyle !== 'none' || !isSupportedModule) return null;
  const dotType = style.moduleStyle as 'square' | 'rounded' | 'dots';
  const module = await import('qr-code-styling');
  const QRCodeStyling = module.default as unknown as new (options: Record<string, unknown>) => { getRawData: (extension: 'svg') => Promise<Blob | unknown> };
  const renderer = new QRCodeStyling({
    width: 640,
    height: 640,
    data: url,
    margin: style.margin * 8,
    qrOptions: { errorCorrectionLevel: style.errorCorrection },
    dotsOptions: { color: style.darkColor, type: dotType },
    cornersSquareOptions: { color: style.accentColor, type: style.finderStyle === 'rounded' ? 'extra-rounded' : style.finderStyle === 'circle' ? 'dot' : 'square' },
    cornersDotOptions: { color: style.accentColor, type: style.finderStyle === 'circle' ? 'dot' : 'square' },
    backgroundOptions: { color: style.lightColor },
  });
  const raw = await renderer.getRawData('svg');
  return raw instanceof Blob ? raw.text() : null;
}

function colorContrast(left: string, right: string): number {
  const luminance = (hex: string) => {
    const normalized = hex.replace('#', '');
    const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255).map((value) => value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
    return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
  };
  const a = luminance(left); const b = luminance(right);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
